import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Link,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signIn, getCurrentUser, createRecovery, RecoveryPassword } from '../lib/appwrite';
import { useGlobalContext } from '../context/GlobalProvider';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setIsLogged, setUser } = useGlobalContext();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openForgotModal, setOpenForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const recoveryUserId = searchParams.get('userId');
  const recoverySecret = searchParams.get('secret');
  const isRecoveryMode = Boolean(recoveryUserId && recoverySecret);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryConfirm, setRecoveryConfirm] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    if (!forgotEmail?.trim()) {
      setForgotError('Ingresa tu correo electrónico.');
      return;
    }
    setForgotLoading(true);
    try {
      const redirectUrl = `${window.location.origin}${window.location.pathname || '/login'}`;
      await createRecovery(forgotEmail.trim(), redirectUrl);
      setForgotSuccess('Revisa tu correo. Te enviamos un enlace para restablecer tu contraseña (válido 1 hora).');
      setForgotEmail('');
    } catch (err) {
      setForgotError(err.message || 'Error al enviar el correo de recuperación.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    setRecoveryError('');
    if (!recoveryPassword || recoveryPassword.length < 8) {
      setRecoveryError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (recoveryPassword !== recoveryConfirm) {
      setRecoveryError('Las contraseñas no coinciden.');
      return;
    }
    setRecoveryLoading(true);
    try {
      await RecoveryPassword(recoveryUserId, recoverySecret, recoveryPassword);
      setRecoverySuccess(true);
      setSearchParams({});
      setRecoveryPassword('');
      setRecoveryConfirm('');
    } catch (err) {
      setRecoveryError(err.message || 'Error al restablecer la contraseña.');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      console.log('Attempting to sign in with:', formData.email);
      const session = await signIn(formData.email, formData.password);
      console.log('Sign in successful, session:', session);
      
      try {
        const currentUser = await getCurrentUser();
        console.log('Current user retrieved:', currentUser);
        
        if (currentUser) {
          setIsLogged(true);
          setUser(currentUser);
          navigate('/home');
        } else {
          setError('No se encontró la información del usuario. Por favor, contacta al administrador.');
        }
      } catch (userError) {
        console.error('Error getting user data:', userError);
        setError(userError.message || 'Error al obtener la información del usuario');
      }
    } catch (err) {
      console.error('Login error:', err);
      const msg = err?.message || '';
      if (msg.includes('Invalid credentials') || msg.includes('email and password')) {
        setError('Credenciales inválidas. Verifica el correo y la contraseña.');
      } else {
        setError(msg || 'Error al iniciar sesión. Por favor, verifica tus credenciales.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${require('../assets/bg.jpg')})`,
          backgroundSize: 'cover',
          backgroundPosition: 'bottom',
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
          height: '100hv',
        }}
      />
      <Container maxWidth="lg" sx={{ height: '100%', py: 4 }}>
        <Grid container spacing={2} sx={{ height: '100%', minHeight: 'calc(100vh - 200px)' }}>
          {/* Left side - Logo */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                borderRadius: 2,
                p: 4,
                gap: 2,
              }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  whiteSpace: 'pre-line',
                }}
              >
                Impulsando el deporte{'\n'}
                que{' '}
                <Typography
                  component="span"
                  variant="h3"
                  sx={{
                    color: 'secondary.main',
                    fontWeight: 'bold',
                  }}
                >
                  amamos
                </Typography>
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src={require('../assets/logo-ags.png')}
                  alt="Logo Asociación de Golf del Sur"
                  style={{ maxWidth: '80%', height: 'auto' }}
                />
              </Box>
              <Typography
                component="span"
                variant="h3"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 'bold',
                }}
              >
                #ElGolfNosUne
              </Typography>
            </Box>
          </Grid>

          {/* Right side - Login Form */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  width: '100%',
                  maxWidth: 400,
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Typography variant="h4" component="h2" gutterBottom align="center">
                  Bienvenido
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom align="center">
                  {isRecoveryMode ? 'Restablece tu contraseña' : 'Inicia sesión para continuar'}
                </Typography>

                {isRecoveryMode ? (
                  <Box component="form" onSubmit={handleRecoverySubmit} sx={{ mt: 3 }}>
                    {recoverySuccess && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Contraseña actualizada. Ya puedes iniciar sesión.
                      </Alert>
                    )}
                    {!recoverySuccess && (
                      <>
                        {recoveryError && (
                          <Alert severity="error" sx={{ mb: 2 }}>{recoveryError}</Alert>
                        )}
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label="Nueva contraseña"
                          type="password"
                          autoComplete="new-password"
                          value={recoveryPassword}
                          onChange={(e) => setRecoveryPassword(e.target.value)}
                          disabled={recoveryLoading}
                        />
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label="Confirmar contraseña"
                          type="password"
                          autoComplete="new-password"
                          value={recoveryConfirm}
                          onChange={(e) => setRecoveryConfirm(e.target.value)}
                          disabled={recoveryLoading}
                        />
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          sx={{ mt: 3, mb: 2 }}
                          disabled={recoveryLoading}
                        >
                          {recoveryLoading ? 'Guardando...' : 'Restablecer contraseña'}
                        </Button>
                      </>
                    )}
                  </Box>
                ) : (
                  <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                      </Alert>
                    )}
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="Email"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Contraseña"
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isLoading}
                    />
                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => setOpenForgotModal(true)}
                        sx={{
                          color: '#d32f2f',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          '&:hover': { color: '#b71c1c' },
                        }}
                      >
                        ¿Olvidaste tu contraseña?
                      </Link>
                    </Box>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                    </Button>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">
                        ¿No tienes cuenta?{' '}
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate('/register')}
                          sx={{ cursor: 'pointer' }}
                        >
                          Registrate
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Dialog open={openForgotModal} onClose={() => { setOpenForgotModal(false); setForgotError(''); setForgotSuccess(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>¿Olvidaste tu contraseña?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1, mb: 2 }}>
            Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña (válido 1 hora).
          </Typography>
          <Box component="form" onSubmit={handleForgotSubmit}>
            {forgotSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>{forgotSuccess}</Alert>
            )}
            {forgotError && (
              <Alert severity="error" sx={{ mb: 2 }}>{forgotError}</Alert>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              label="Correo electrónico"
              type="email"
              autoComplete="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              disabled={forgotLoading}
            />
            <DialogActions sx={{ px: 0, pt: 2 }}>
              <Button onClick={() => { setOpenForgotModal(false); setForgotError(''); setForgotSuccess(''); }} variant="outlined">
                Cancelar
              </Button>
              <Button type="submit" variant="contained" color="primary" disabled={forgotLoading}>
                {forgotLoading ? 'Enviando...' : 'Enviar enlace'}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Login;